import React, { useContext, useEffect, useState } from "react";
import {
  Button,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  makeStyles,
} from "@material-ui/core";
import { Add, Colorize, DeleteOutline } from "@material-ui/icons";
import { toast } from "react-toastify";

import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import { canShowMenu } from "../../helpers/menuAccess";
import useSettings from "../../hooks/useSettings";
import ColorPicker from "../ColorPicker";

const KANBAN_INACTIVITY_PERMISSION = "kanban_config_tempo_inatividade_cards";
const KANBAN_CONTACT_METHODS_SETTING_KEY = "kanbanEnabledRelationshipContactMethods";
const LEGACY_CONTACT_METHOD_LABELS = {
  VISITA: "Visita",
  ORCAMENTO: "Orçamento",
  FOLLOW_UP: "Follow-up",
  NEGOCIACAO: "Negociação",
  POS_VENDA: "Pós-venda",
  RECLAMACAO: "Reclamação",
  INDICACAO: "Indicação",
};
const DEFAULT_KANBAN_CONTACT_METHODS = [
  "Visita",
  "Orçamento",
  "Follow-up",
  "Negociação",
  "Pós-venda",
  "Reclamação",
  "Indicação",
  "Tráfego Pago",
  "Prospecção",
];

const normalizeContactMethodLabel = (value) => String(value || "").replace(/\s+/g, " ").trim();

const dedupeContactMethods = (items = []) => {
  const unique = [];
  const seen = new Set();

  for (const item of items) {
    const normalized = normalizeContactMethodLabel(item);
    if (!normalized) continue;

    const key = normalized
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase();

    if (seen.has(key)) continue;

    seen.add(key);
    unique.push(normalized);
  }

  return unique;
};

const parseEnabledContactMethods = (rawValue) => {
  if (!rawValue) {
    return DEFAULT_KANBAN_CONTACT_METHODS;
  }

  try {
    const parsed = JSON.parse(rawValue);
    if (!Array.isArray(parsed)) {
      return DEFAULT_KANBAN_CONTACT_METHODS;
    }

    const normalized = dedupeContactMethods(
      parsed.map((value) => {
        const raw = normalizeContactMethodLabel(value);
        const legacyLabel = LEGACY_CONTACT_METHOD_LABELS[String(raw).toUpperCase()];
        return legacyLabel || raw;
      })
    );

    return normalized.length > 0 ? Array.from(new Set(normalized)) : DEFAULT_KANBAN_CONTACT_METHODS;
  } catch (_error) {
    return DEFAULT_KANBAN_CONTACT_METHODS;
  }
};

const useStyles = makeStyles((theme) => ({
  sectionTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(0.5),
  },
  sectionDescription: {
    marginBottom: theme.spacing(2),
  },
  selectContainer: {
    width: "100%",
    textAlign: "left",
  },
  colorAdornment: {
    width: 20,
    height: 20,
    borderRadius: 4,
    border: "1px solid #dcdcdc",
  },
}));

const KanbanSettings = ({ settings = [] }) => {
  const classes = useStyles();
  const { user, menus } = useContext(AuthContext);
  const { update } = useSettings();

  const canManageInactivityTimer = canShowMenu(
    user,
    menus,
    KANBAN_INACTIVITY_PERMISSION
  );

  const [requireScheduleOnCardCreation, setRequireScheduleOnCardCreation] = useState("enabled");
  const [kanbanCardCreationMode, setKanbanCardCreationMode] = useState("optional");
  const [kanbanDuplicateContactCard, setKanbanDuplicateContactCard] = useState("disabled");
  const [kanbanDuplicateContactScope, setKanbanDuplicateContactScope] = useState("column");
  const [webhookKommoSendCodcli, setWebhookKommoSendCodcli] = useState("disabled");

  const [kanbanInactivityWarningDays, setKanbanInactivityWarningDays] = useState("1");
  const [kanbanInactivityDangerDays, setKanbanInactivityDangerDays] = useState("3");
  const [kanbanInactivityCriticalDays, setKanbanInactivityCriticalDays] = useState("5");
  const [kanbanInactivityHighlightEnabled, setKanbanInactivityHighlightEnabled] = useState("disabled");

  const [kanbanInactivityWarningColor, setKanbanInactivityWarningColor] = useState("#ffeb3b");
  const [kanbanInactivityDangerColor, setKanbanInactivityDangerColor] = useState("#f44336");
  const [kanbanInactivityCriticalColor, setKanbanInactivityCriticalColor] = useState("#000000");
  const [enabledContactMethods, setEnabledContactMethods] = useState(DEFAULT_KANBAN_CONTACT_METHODS);
  const [newContactMethod, setNewContactMethod] = useState("");

  const [colorPickerTarget, setColorPickerTarget] = useState("");

  const [loadingSchedule, setLoadingSchedule] = useState(false);
  const [loadingCreationMode, setLoadingCreationMode] = useState(false);
  const [loadingDuplicateContact, setLoadingDuplicateContact] = useState(false);
  const [loadingCodcli, setLoadingCodcli] = useState(false);
  const [loadingContactMethods, setLoadingContactMethods] = useState(false);
  const [loadingInactivity, setLoadingInactivity] = useState(false);

  useEffect(() => {
    const read = (key, fallback) => {
      const found = settings.find((s) => s.key === key);
      return found?.value ?? fallback;
    };

    setRequireScheduleOnCardCreation(String(read("requireScheduleOnCardCreation", "enabled")));
    setKanbanCardCreationMode(String(read("kanbanCardCreationMode", "optional")));
    setKanbanDuplicateContactCard(String(read("kanbanDuplicateContactCard", "disabled")));
    setKanbanDuplicateContactScope(String(read("kanbanDuplicateContactScope", "column")));
    setWebhookKommoSendCodcli(String(read("webhookKommoSendCodcli", "disabled")));
    setKanbanInactivityHighlightEnabled(String(read("kanbanInactivityHighlightEnabled", "disabled")));

    setKanbanInactivityWarningDays(String(read("kanbanInactivityWarningDays", "1")));
    setKanbanInactivityDangerDays(String(read("kanbanInactivityDangerDays", "3")));
    setKanbanInactivityCriticalDays(String(read("kanbanInactivityCriticalDays", "5")));

    setKanbanInactivityWarningColor(String(read("kanbanInactivityWarningColor", "#ffeb3b")));
    setKanbanInactivityDangerColor(String(read("kanbanInactivityDangerColor", "#f44336")));
    setKanbanInactivityCriticalColor(String(read("kanbanInactivityCriticalColor", "#000000")));
    setEnabledContactMethods(parseEnabledContactMethods(read(KANBAN_CONTACT_METHODS_SETTING_KEY, JSON.stringify(DEFAULT_KANBAN_CONTACT_METHODS))));
  }, [settings]);

  const saveContactMethods = async (nextMethods) => {
    const deduped = dedupeContactMethods(nextMethods);
    if (deduped.length === 0) {
      toast.error(i18n.t("settings.kanban.errors.contactMethodsEmpty"));
      return false;
    }

    setEnabledContactMethods(deduped);
    await handleUpdateSelect({
      key: KANBAN_CONTACT_METHODS_SETTING_KEY,
      value: JSON.stringify(deduped),
      setLoading: setLoadingContactMethods,
      successKey: "settings.kanban.messages.contactMethodsSaved",
    });
    return true;
  };

  const handleChangeContactMethod = (index, value) => {
    setEnabledContactMethods((prev) => prev.map((item, idx) => (idx === index ? value : item)));
  };

  const handleBlurContactMethod = async () => {
    await saveContactMethods(enabledContactMethods);
  };

  const handleRemoveContactMethod = async (index) => {
    const nextMethods = enabledContactMethods.filter((_item, idx) => idx !== index);
    await saveContactMethods(nextMethods);
  };

  const handleAddContactMethod = async () => {
    const normalized = normalizeContactMethodLabel(newContactMethod);
    if (!normalized) return;

    const success = await saveContactMethods([...enabledContactMethods, normalized]);
    if (success) {
      setNewContactMethod("");
    }
  };

  const handleUpdateSelect = async ({ key, value, setLoading, successKey }) => {
    setLoading(true);
    try {
      await update({ key, value });
      toast.success(i18n.t(successKey));
    } catch (error) {
      toast.error(i18n.t("settings.kanban.errors.update"));
    }
    setLoading(false);
  };

  const saveInactivityDays = async ({ key, rawValue, setState }) => {
    const parsed = Number(rawValue);
    if (!Number.isFinite(parsed) || parsed < 0) {
      toast.error(i18n.t("settings.kanban.errors.invalidDays"));
      return;
    }

    const normalizedDays = Math.floor(parsed);
    const currentWarning = Math.floor(Number(kanbanInactivityWarningDays) || 0);
    const currentDanger = Math.floor(Number(kanbanInactivityDangerDays) || 0);
    const currentCritical = Math.floor(Number(kanbanInactivityCriticalDays) || 0);

    const nextWarning = key === "kanbanInactivityWarningDays" ? normalizedDays : currentWarning;
    const nextDanger = key === "kanbanInactivityDangerDays" ? normalizedDays : currentDanger;
    const nextCritical = key === "kanbanInactivityCriticalDays" ? normalizedDays : currentCritical;

    if (!(nextWarning <= nextDanger && nextDanger <= nextCritical)) {
      toast.error(i18n.t("settings.kanban.errors.invalidDaysOrder"));
      return;
    }

    const normalized = String(normalizedDays);
    setState(normalized);
    setLoadingInactivity(true);
    try {
      await update({ key, value: normalized });
      toast.success(i18n.t("settings.kanban.messages.inactivitySaved"));
    } catch (error) {
      toast.error(i18n.t("settings.kanban.errors.update"));
    }
    setLoadingInactivity(false);
  };

  const saveInactivityColor = async ({ key, value, setState }) => {
    const validHex = /^#[0-9a-fA-F]{6}$/.test(value || "");
    if (!validHex) {
      toast.error(i18n.t("settings.kanban.errors.invalidColor"));
      return;
    }

    setState(value);
    setLoadingInactivity(true);
    try {
      await update({ key, value });
      toast.success(i18n.t("settings.kanban.messages.inactivitySaved"));
    } catch (error) {
      toast.error(i18n.t("settings.kanban.errors.update"));
    }
    setLoadingInactivity(false);
  };

  const handleColorChange = (target, color) => {
    if (target === "warning") {
      saveInactivityColor({
        key: "kanbanInactivityWarningColor",
        value: color,
        setState: setKanbanInactivityWarningColor,
      });
      return;
    }

    if (target === "danger") {
      saveInactivityColor({
        key: "kanbanInactivityDangerColor",
        value: color,
        setState: setKanbanInactivityDangerColor,
      });
      return;
    }

    saveInactivityColor({
      key: "kanbanInactivityCriticalColor",
      value: color,
      setState: setKanbanInactivityCriticalColor,
    });
  };

  const colorPickerCurrentColor =
    colorPickerTarget === "warning"
      ? kanbanInactivityWarningColor
      : colorPickerTarget === "danger"
      ? kanbanInactivityDangerColor
      : kanbanInactivityCriticalColor;

  return (
    <Grid spacing={3} container>
      <Grid xs={12} item>
        <Typography variant="h6" className={classes.sectionTitle}>
          {i18n.t("settings.kanban.title")}
        </Typography>
        <Typography variant="body2" color="textSecondary" className={classes.sectionDescription}>
          {i18n.t("settings.kanban.description")}
        </Typography>
      </Grid>

      <Grid xs={12} sm={6} md={4} item>
        <FormControl className={classes.selectContainer}>
          <InputLabel id="requireScheduleOnCardCreation-label">
            {i18n.t("settings.kanban.requireSchedule.label")}
          </InputLabel>
          <Select
            labelId="requireScheduleOnCardCreation-label"
            value={requireScheduleOnCardCreation}
            onChange={(e) => {
              const value = e.target.value;
              setRequireScheduleOnCardCreation(value);
              handleUpdateSelect({
                key: "requireScheduleOnCardCreation",
                value,
                setLoading: setLoadingSchedule,
                successKey: "settings.kanban.messages.scheduleSaved",
              });
            }}
          >
            <MenuItem value={"disabled"}>{i18n.t("settings.kanban.options.disabled")}</MenuItem>
            <MenuItem value={"enabled"}>{i18n.t("settings.kanban.options.enabled")}</MenuItem>
          </Select>
          <FormHelperText>
            {loadingSchedule ? i18n.t("settings.kanban.loading") : i18n.t("settings.kanban.requireSchedule.helper")}
          </FormHelperText>
        </FormControl>
      </Grid>

      <Grid xs={12} sm={6} md={4} item>
        <FormControl className={classes.selectContainer}>
          <InputLabel id="kanbanCardCreationMode-label">
            {i18n.t("settings.kanban.creationMode.label")}
          </InputLabel>
          <Select
            labelId="kanbanCardCreationMode-label"
            value={kanbanCardCreationMode}
            onChange={(e) => {
              const value = e.target.value;
              setKanbanCardCreationMode(value);
              handleUpdateSelect({
                key: "kanbanCardCreationMode",
                value,
                setLoading: setLoadingCreationMode,
                successKey: "settings.kanban.messages.creationModeSaved",
              });
            }}
          >
            <MenuItem value={"optional"}>{i18n.t("settings.kanban.creationMode.options.optional")}</MenuItem>
            <MenuItem value={"mandatory"}>{i18n.t("settings.kanban.creationMode.options.mandatory")}</MenuItem>
            <MenuItem value={"hidden"}>{i18n.t("settings.kanban.creationMode.options.hidden")}</MenuItem>
          </Select>
          <FormHelperText>
            {loadingCreationMode ? i18n.t("settings.kanban.loading") : i18n.t("settings.kanban.creationMode.helper")}
          </FormHelperText>
        </FormControl>
      </Grid>

      <Grid xs={12} sm={6} md={4} item>
        <FormControl className={classes.selectContainer}>
          <InputLabel id="kanbanDuplicateContactCard-label">
            {i18n.t("settings.kanban.duplicateContact.label")}
          </InputLabel>
          <Select
            labelId="kanbanDuplicateContactCard-label"
            value={kanbanDuplicateContactCard}
            onChange={(e) => {
              const value = e.target.value;
              setKanbanDuplicateContactCard(value);
              handleUpdateSelect({
                key: "kanbanDuplicateContactCard",
                value,
                setLoading: setLoadingDuplicateContact,
                successKey: "settings.kanban.messages.duplicateContactSaved",
              });
            }}
          >
            <MenuItem value={"disabled"}>{i18n.t("settings.kanban.duplicateContact.options.disabled")}</MenuItem>
            <MenuItem value={"warn"}>{i18n.t("settings.kanban.duplicateContact.options.warn")}</MenuItem>
            <MenuItem value={"blocked"}>{i18n.t("settings.kanban.duplicateContact.options.blocked")}</MenuItem>
          </Select>
          <FormHelperText>
            {loadingDuplicateContact ? i18n.t("settings.kanban.loading") : i18n.t("settings.kanban.duplicateContact.helper")}
          </FormHelperText>
        </FormControl>
      </Grid>

      {/* O escopo só faz sentido com a verificação ligada */}
      {kanbanDuplicateContactCard !== "disabled" && (
      <Grid xs={12} sm={6} md={4} item>
        <FormControl className={classes.selectContainer}>
          <InputLabel id="kanbanDuplicateContactScope-label">
            {i18n.t("settings.kanban.duplicateContactScope.label")}
          </InputLabel>
          <Select
            labelId="kanbanDuplicateContactScope-label"
            value={kanbanDuplicateContactScope}
            onChange={(e) => {
              const value = e.target.value;
              setKanbanDuplicateContactScope(value);
              handleUpdateSelect({
                key: "kanbanDuplicateContactScope",
                value,
                setLoading: setLoadingDuplicateContact,
                successKey: "settings.kanban.messages.duplicateContactSaved",
              });
            }}
          >
            <MenuItem value={"column"}>{i18n.t("settings.kanban.duplicateContactScope.options.column")}</MenuItem>
            <MenuItem value={"board"}>{i18n.t("settings.kanban.duplicateContactScope.options.board")}</MenuItem>
            <MenuItem value={"any"}>{i18n.t("settings.kanban.duplicateContactScope.options.any")}</MenuItem>
          </Select>
          <FormHelperText>
            {loadingDuplicateContact ? i18n.t("settings.kanban.loading") : i18n.t("settings.kanban.duplicateContactScope.helper")}
          </FormHelperText>
        </FormControl>
      </Grid>
      )}

      <Grid xs={12} sm={6} md={4} item>
        <FormControl className={classes.selectContainer}>
          <InputLabel id="webhookKommoSendCodcli-label">
            {i18n.t("settings.kanban.codcli.label")}
          </InputLabel>
          <Select
            labelId="webhookKommoSendCodcli-label"
            value={webhookKommoSendCodcli}
            onChange={(e) => {
              const value = e.target.value;
              setWebhookKommoSendCodcli(value);
              handleUpdateSelect({
                key: "webhookKommoSendCodcli",
                value,
                setLoading: setLoadingCodcli,
                successKey: "settings.kanban.messages.codcliSaved",
              });
            }}
          >
            <MenuItem value={"enabled"}>{i18n.t("settings.kanban.options.enabled")}</MenuItem>
            <MenuItem value={"disabled"}>{i18n.t("settings.kanban.options.disabled")}</MenuItem>
          </Select>
          <FormHelperText>
            {loadingCodcli ? i18n.t("settings.kanban.loading") : i18n.t("settings.kanban.codcli.helper")}
          </FormHelperText>
        </FormControl>
      </Grid>

      <Grid xs={12} item>
        <Typography variant="subtitle1" className={classes.sectionTitle}>
          {i18n.t("settings.kanban.contactMethods.title")}
        </Typography>
        <Typography variant="body2" color="textSecondary" className={classes.sectionDescription}>
          {i18n.t("settings.kanban.contactMethods.description")}
        </Typography>
        <Grid container spacing={1}>
          {enabledContactMethods.map((contactMethod, index) => (
            <Grid key={`${contactMethod}-${index}`} xs={12} sm={6} md={4} item>
              <TextField
                fullWidth
                size="small"
                variant="outlined"
                value={contactMethod}
                onChange={(event) => handleChangeContactMethod(index, event.target.value)}
                onBlur={handleBlurContactMethod}
                placeholder={i18n.t("settings.kanban.contactMethods.placeholder")}
                InputProps={{
                  endAdornment: (
                    <IconButton
                      size="small"
                      onClick={() => handleRemoveContactMethod(index)}
                      aria-label={i18n.t("settings.kanban.contactMethods.removeButton")}
                    >
                      <DeleteOutline fontSize="small" />
                    </IconButton>
                  ),
                }}
              />
            </Grid>
          ))}
          <Grid xs={12} sm={6} md={4} item>
            <TextField
              fullWidth
              size="small"
              variant="outlined"
              value={newContactMethod}
              onChange={(event) => setNewContactMethod(event.target.value)}
              placeholder={i18n.t("settings.kanban.contactMethods.placeholder")}
            />
          </Grid>
          <Grid xs={12} sm={6} md={4} item>
            <Button
              variant="outlined"
              color="primary"
              fullWidth
              startIcon={<Add />}
              onClick={handleAddContactMethod}
              disabled={loadingContactMethods}
            >
              {i18n.t("settings.kanban.contactMethods.addButton")}
            </Button>
          </Grid>
        </Grid>
        <FormHelperText>
          {loadingContactMethods
            ? i18n.t("settings.kanban.loading")
            : i18n.t("settings.kanban.contactMethods.helper")}
        </FormHelperText>
      </Grid>

      <Grid xs={12} item>
        <Typography variant="subtitle1" className={classes.sectionTitle}>
          {i18n.t("settings.kanban.inactivity.title")}
        </Typography>
        <Typography variant="body2" color="textSecondary" className={classes.sectionDescription}>
          {i18n.t("settings.kanban.inactivity.description")}
        </Typography>
      </Grid>

      {canManageInactivityTimer ? (
        <>
          <Grid xs={12} sm={6} md={4} item>
            <FormControl className={classes.selectContainer}>
              <InputLabel id="kanbanInactivityHighlightEnabled-label">
                {i18n.t("settings.kanban.inactivity.enableLabel")}
              </InputLabel>
              <Select
                labelId="kanbanInactivityHighlightEnabled-label"
                value={kanbanInactivityHighlightEnabled}
                onChange={(e) => {
                  const value = e.target.value;
                  setKanbanInactivityHighlightEnabled(value);
                  handleUpdateSelect({
                    key: "kanbanInactivityHighlightEnabled",
                    value,
                    setLoading: setLoadingInactivity,
                    successKey: "settings.kanban.messages.inactivitySaved",
                  });
                }}
              >
                <MenuItem value={"disabled"}>{i18n.t("settings.kanban.options.disabled")}</MenuItem>
                <MenuItem value={"enabled"}>{i18n.t("settings.kanban.options.enabled")}</MenuItem>
              </Select>
              <FormHelperText>
                {loadingInactivity ? i18n.t("settings.kanban.loading") : i18n.t("settings.kanban.inactivity.enableHelper")}
              </FormHelperText>
            </FormControl>
          </Grid>

          {kanbanInactivityHighlightEnabled === "enabled" && (
            <>
          <Grid xs={12} item>
            <Typography variant="body2" color="textSecondary" className={classes.sectionDescription}>
              {i18n.t("settings.kanban.inactivity.rules")}
            </Typography>
          </Grid>

          <Grid xs={12} item>
            <Grid container spacing={1}>
              <Grid xs={12} sm={6} md={2} item>
                <TextField
                  className={classes.selectContainer}
                  label={i18n.t("settings.kanban.inactivity.warningDays")}
                  type="number"
                  variant="outlined"
                  size="small"
                  value={kanbanInactivityWarningDays}
                  onChange={(e) => setKanbanInactivityWarningDays(e.target.value)}
                  onBlur={() =>
                    saveInactivityDays({
                      key: "kanbanInactivityWarningDays",
                      rawValue: kanbanInactivityWarningDays,
                      setState: setKanbanInactivityWarningDays,
                    })
                  }
                  inputProps={{ min: 0 }}
                />
              </Grid>

              <Grid xs={12} sm={6} md={2} item>
                <TextField
                  className={classes.selectContainer}
                  label={i18n.t("settings.kanban.inactivity.warningColor")}
                  name="kanbanInactivityWarningColor"
                  value={kanbanInactivityWarningColor}
                  onFocus={() => setColorPickerTarget("warning")}
                  onChange={(e) => setKanbanInactivityWarningColor(e.target.value)}
                  onBlur={() =>
                    saveInactivityColor({
                      key: "kanbanInactivityWarningColor",
                      value: kanbanInactivityWarningColor,
                      setState: setKanbanInactivityWarningColor,
                    })
                  }
                  variant="outlined"
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <div style={{ backgroundColor: kanbanInactivityWarningColor }} className={classes.colorAdornment} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <IconButton size="small" color="default" onClick={() => setColorPickerTarget("warning")}>
                        <Colorize />
                      </IconButton>
                    ),
                  }}
                />
              </Grid>

              <Grid xs={12} sm={6} md={2} item>
                <TextField
                  className={classes.selectContainer}
                  label={i18n.t("settings.kanban.inactivity.dangerDays")}
                  type="number"
                  variant="outlined"
                  size="small"
                  value={kanbanInactivityDangerDays}
                  onChange={(e) => setKanbanInactivityDangerDays(e.target.value)}
                  onBlur={() =>
                    saveInactivityDays({
                      key: "kanbanInactivityDangerDays",
                      rawValue: kanbanInactivityDangerDays,
                      setState: setKanbanInactivityDangerDays,
                    })
                  }
                  inputProps={{ min: 0 }}
                />
              </Grid>

              <Grid xs={12} sm={6} md={2} item>
                <TextField
                  className={classes.selectContainer}
                  label={i18n.t("settings.kanban.inactivity.dangerColor")}
                  name="kanbanInactivityDangerColor"
                  value={kanbanInactivityDangerColor}
                  onFocus={() => setColorPickerTarget("danger")}
                  onChange={(e) => setKanbanInactivityDangerColor(e.target.value)}
                  onBlur={() =>
                    saveInactivityColor({
                      key: "kanbanInactivityDangerColor",
                      value: kanbanInactivityDangerColor,
                      setState: setKanbanInactivityDangerColor,
                    })
                  }
                  variant="outlined"
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <div style={{ backgroundColor: kanbanInactivityDangerColor }} className={classes.colorAdornment} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <IconButton size="small" color="default" onClick={() => setColorPickerTarget("danger")}>
                        <Colorize />
                      </IconButton>
                    ),
                  }}
                />
              </Grid>

              <Grid xs={12} sm={6} md={2} item>
                <TextField
                  className={classes.selectContainer}
                  label={i18n.t("settings.kanban.inactivity.criticalDays")}
                  type="number"
                  variant="outlined"
                  size="small"
                  value={kanbanInactivityCriticalDays}
                  onChange={(e) => setKanbanInactivityCriticalDays(e.target.value)}
                  onBlur={() =>
                    saveInactivityDays({
                      key: "kanbanInactivityCriticalDays",
                      rawValue: kanbanInactivityCriticalDays,
                      setState: setKanbanInactivityCriticalDays,
                    })
                  }
                  inputProps={{ min: 0 }}
                />
              </Grid>

              <Grid xs={12} sm={6} md={2} item>
                <TextField
                  className={classes.selectContainer}
                  label={i18n.t("settings.kanban.inactivity.criticalColor")}
                  name="kanbanInactivityCriticalColor"
                  value={kanbanInactivityCriticalColor}
                  onFocus={() => setColorPickerTarget("critical")}
                  onChange={(e) => setKanbanInactivityCriticalColor(e.target.value)}
                  onBlur={() =>
                    saveInactivityColor({
                      key: "kanbanInactivityCriticalColor",
                      value: kanbanInactivityCriticalColor,
                      setState: setKanbanInactivityCriticalColor,
                    })
                  }
                  variant="outlined"
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <div style={{ backgroundColor: kanbanInactivityCriticalColor }} className={classes.colorAdornment} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <IconButton size="small" color="default" onClick={() => setColorPickerTarget("critical")}>
                        <Colorize />
                      </IconButton>
                    ),
                  }}
                />
              </Grid>
            </Grid>
          </Grid>

          <ColorPicker
            open={Boolean(colorPickerTarget)}
            currentColor={colorPickerCurrentColor}
            handleClose={() => setColorPickerTarget("")}
            onChange={(color) => handleColorChange(colorPickerTarget, color)}
          />
            </>
          )}
        </>
      ) : (
        <Grid xs={12} item>
          <Typography variant="body2" color="textSecondary">
            {i18n.t("settings.kanban.inactivity.noPermission")}
          </Typography>
        </Grid>
      )}
    </Grid>
  );
};

export default KanbanSettings;
